import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import PostMioLogo from '../components/PostMioLogo';

// ── i18n ───────────────────────────────────────────────────
const UI_TEXT = {
  IT: {
    subtitle:    'Bentornato',
    errorCreds:  'Credenziali non valide. Riprova.',
    btnLoading:  'Accesso in corso...',
    btnSubmit:   'Entra nel Dashboard',
    noAccount:   'Non hai un account?',
    startTrial:  'Inizia la prova gratuita',
  },
  EN: {
    subtitle:    'Welcome back',
    errorCreds:  'Invalid credentials. Please try again.',
    btnLoading:  'Signing in...',
    btnSubmit:   'Go to Dashboard',
    noAccount:   'Don\'t have an account?',
    startTrial:  'Start free trial',
  },
  ES: {
    subtitle:    'Bienvenido de nuevo',
    errorCreds:  'Credenciales no válidas. Por favor, inténtalo de nuevo.',
    btnLoading:  'Iniciando sesión...',
    btnSubmit:   'Ir al Panel',
    noAccount:   '¿No tienes una cuenta?',
    startTrial:  'Iniciar prueba gratuita',
  },
  DE: {
    subtitle:    'Willkommen zurück',
    errorCreds:  'Ungültige Anmeldedaten. Bitte versuchen Sie es erneut.',
    btnLoading:  'Anmeldung läuft...',
    btnSubmit:   'Zum Dashboard',
    noAccount:   'Kein Konto?',
    startTrial:  'Kostenlose Testversion starten',
  },
  FR: {
    subtitle:    'Bon retour',
    errorCreds:  'Identifiants invalides. Veuillez réessayer.',
    btnLoading:  'Connexion en cours...',
    btnSubmit:   'Aller au tableau de bord',
    noAccount:   'Pas encore de compte ?',
    startTrial:  'Démarrer l\'essai gratuit',
  },
  PL: {
    subtitle:    'Witaj ponownie',
    errorCreds:  'Nieprawidłowe dane logowania. Spróbuj ponownie.',
    btnLoading:  'Logowanie...',
    btnSubmit:   'Przejdź do panelu',
    noAccount:   'Nie masz konta?',
    startTrial:  'Rozpocznij darmowy okres próbny',
  },
}

function detectLang() {
  const lang = (navigator.language || '').toLowerCase()
  if (lang.startsWith('pl')) return 'PL'
  if (lang.startsWith('en')) return 'EN'
  if (lang.startsWith('es')) return 'ES'
  if (lang.startsWith('de')) return 'DE'
  if (lang.startsWith('fr')) return 'FR'
  return 'IT'
}

export default function Login() {
  const t = UI_TEXT[detectLang()]
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [showForgot, setShowForgot]     = useState(false);
  const [forgotEmail, setForgotEmail]   = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent]     = useState(false);
  const [forgotError, setForgotError]   = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate('/dashboard');
    });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) {
      setError(t.errorCreds);
      setLoading(false);
    } else {
      window.location.href = '/dashboard';
    }
  };

  const handleForgot = async () => {
    setForgotLoading(true);
    setForgotError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
      redirectTo: 'https://www.postmio.com/reset-password',
    });
    if (error) {
      setForgotError(error.message);
    } else {
      setForgotSent(true);
    }
    setForgotLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-4"
      style={{ fontFamily: 'DM Sans, sans-serif' }}
    >
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-block">
            <PostMioLogo size="md" variant="dark" />
          </Link>
          <p className="text-[#7A7A7A] text-xs uppercase tracking-[0.3em] mt-2">
            {t.subtitle}
          </p>
        </div>

        {/* Card */}
        <div className="bg-[#141414] border border-[#2C2C2C] rounded-2xl p-8 shadow-xl">

          {error && (
            <div className="mb-6 p-4 bg-[#A02020]/10 border border-[#A02020]/30 rounded-xl text-sm text-[#F0EDE8] text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              className="w-full bg-[#0A0A0A] border border-[#2C2C2C] rounded-xl px-5 py-4 text-[#F0EDE8] text-sm placeholder-[#606060] focus:border-[#C9922A] outline-none transition-all"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              type="password"
              placeholder="Password"
              className="w-full bg-[#0A0A0A] border border-[#2C2C2C] rounded-xl px-5 py-4 text-[#F0EDE8] text-sm placeholder-[#606060] focus:border-[#C9922A] outline-none transition-all"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            {/* Forgot password */}
            <div>
              <button
                type="button"
                onClick={() => { setShowForgot(v => !v); setForgotSent(false); setForgotError(null); }}
                className="text-xs transition-colors"
                style={{ color: '#7A7A7A', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'none' }}
                onMouseEnter={e => e.currentTarget.style.color = '#C9922A'}
                onMouseLeave={e => e.currentTarget.style.color = '#7A7A7A'}
              >
                Password dimenticata?
              </button>

              {showForgot && (
                <div className="mt-3 space-y-3">
                  {forgotSent ? (
                    <p className="text-xs" style={{ color: '#7A7A7A' }}>
                      Email inviata. Controlla la tua casella.
                    </p>
                  ) : (
                    <>
                      {forgotError && (
                        <p className="text-xs text-[#A02020]">{forgotError}</p>
                      )}
                      <input
                        type="email"
                        placeholder="Email"
                        className="w-full bg-[#0A0A0A] border border-[#2C2C2C] rounded-xl px-5 py-4 text-[#F0EDE8] text-sm placeholder-[#606060] focus:border-[#C9922A] outline-none transition-all"
                        value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={handleForgot}
                        disabled={forgotLoading || !forgotEmail.trim()}
                        className="w-full bg-[#C9922A] hover:bg-[#E5B050] disabled:opacity-40 disabled:cursor-not-allowed font-bold py-4 rounded-xl text-sm uppercase tracking-[0.2em] transition-all"
                        style={{ color: '#000000' }}
                      >
                        {forgotLoading ? 'Invio in corso...' : 'Invia link di reset'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C9922A] hover:bg-[#E5B050] disabled:opacity-40 disabled:cursor-not-allowed text-[#0A0A0A] font-bold py-4 rounded-xl text-sm uppercase tracking-[0.2em] transition-all mt-2"
            >
              {loading ? t.btnLoading : t.btnSubmit}
            </button>
          </form>

          <p className="mt-8 text-center text-[#606060] text-xs">
            {t.noAccount}{' '}
            <a href="/pricing" className="text-[#C9922A] hover:text-[#E5B050] transition-colors">
              {t.startTrial}
            </a>
          </p>
          <p className="mt-4 text-center text-xs">
            <a href="/privacy" className="text-[#606060] hover:text-[#A0A0A0] transition-colors">Privacy Policy</a>
          </p>
        </div>

      </div>
    </div>
  );
}
